import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Radio, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { pickLogo, scoresByTeam, formatInnings } from "@/lib/playhq";

const REFRESH_MS = 45_000; // 45s — matches PlayHQ's own live-score cadence

/**
 * Compact "Live now" tile row shown above the Results page. Independently
 * polls the current (non-COMPLETED) PlayHQ season's IN_PROGRESS games so
 * members can peek at scores without waiting for the final whistle.
 *
 * • Silently renders `null` when there are zero live club games — the row
 *   never adds noise unless something is actually happening.
 * • Auto-refreshes every 45s while at least one game is live, so scores
 *   tick without a page reload.
 * • Every tile is clickable and calls `onOpen(gameId)` — the parent wires
 *   this into the existing ScorecardDialog so the tap experience matches
 *   the rest of the Results list.
 */
export default function LiveScorePeek({ currentSeasonId, onOpen }) {
  const [games, setGames] = useState([]);
  const [teamMap, setTeamMap] = useState({});
  const [gradeMap, setGradeMap] = useState({});
  const [clubIds, setClubIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [firstLoadDone, setFirstLoadDone] = useState(false);

  const load = useCallback(async () => {
    if (!currentSeasonId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/playhq/seasons/${currentSeasonId}/club-fixtures`, {
        params: { status: "IN_PROGRESS" },
      });
      setGames(Array.isArray(data?.fixtures) ? data.fixtures : []);
      setTeamMap(data?.team_map || {});
      setGradeMap(data?.grade_map || {});
      setClubIds(new Set(Array.isArray(data?.club_team_ids) ? data.club_team_ids : []));
    } catch {
      // Silent — the live tile is a peek, not a blocking feature.
    } finally {
      setLoading(false);
      setFirstLoadDone(true);
    }
  }, [currentSeasonId]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh while at least one game is live. When the list becomes
  // empty we stop the interval to save requests and let the initial-load
  // effect resume polling if a game later goes live.
  useEffect(() => {
    if (games.length === 0) return undefined;
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [games.length, load]);

  const sortedGames = useMemo(
    () => [...games].sort((a, b) => (a.start_at || "").localeCompare(b.start_at || "")),
    [games]
  );

  // First load hasn't finished, or nothing is live → render nothing so the
  // Results page doesn't collect empty scaffolding.
  if (!currentSeasonId) return null;
  if (!firstLoadDone && !loading) return null;
  if (sortedGames.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="live-score-peek">
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="inline-flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-500/60 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-red-600">
            Live now
          </span>
          {loading && <Loader2 className="h-3 w-3 text-muted-foreground animate-spin" />}
        </div>
        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          {sortedGames.length} game{sortedGames.length === 1 ? "" : "s"}
        </span>
      </div>

      <div
        className="mobile-scroll-x -mx-1 px-1 flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory"
        data-testid="live-score-peek-scroll"
      >
        {sortedGames.map((g) => (
          <LiveGameTile
            key={g.playhq_id}
            game={g}
            teamMap={teamMap}
            gradeMap={gradeMap}
            clubIds={clubIds}
            onOpen={onOpen}
          />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Chase math — when a limited-overs game is on its second innings the tile
// shows "target X · needs Y off Z" under the score so members can gauge
// the state of the run chase at a glance.
// ---------------------------------------------------------------------------
function _stats(teamNode) {
  const arr = teamNode?.outcome?.statistics || [];
  const m = {};
  for (const s of arr) m[s.type] = s.value;
  return m;
}

/**
 * Convert a PlayHQ overs number like `24.3` (24 overs + 3 balls) into
 * absolute balls bowled. Handles integers, floats and stringy inputs;
 * returns `null` when we can't parse it.
 */
function oversToBalls(overs) {
  if (overs == null) return null;
  const num = typeof overs === "string" ? parseFloat(overs) : overs;
  if (!Number.isFinite(num) || num < 0) return null;
  const whole = Math.floor(num);
  const balls = Math.round((num - whole) * 10);
  return whole * 6 + Math.min(balls, 5);
}

/**
 * Current run rate for a single innings snapshot — runs per full over,
 * derived from `runs` and `overs` (PlayHQ's `X.Y` form). Returns `null`
 * when we can't compute (missing runs, zero overs bowled, or unparseable
 * inputs) so the caller can hide the pill entirely.
 */
function currentRunRate(innings) {
  if (!innings) return null;
  const runs = innings.runs;
  if (runs == null) return null;
  const balls = oversToBalls(innings.overs);
  if (balls == null || balls === 0) return null;
  const oversFloat = balls / 6;
  const rate = runs / oversFloat;
  if (!Number.isFinite(rate)) return null;
  return +rate.toFixed(2);
}

/**
 * Returns `{ chasingTeamId, target, needed, ballsLeft, remainderLabel }`
 * when the game is a limited-overs run chase in progress; `null`
 * otherwise (test-match multi-innings, first innings, target reached,
 * innings ended, or missing over-limit data).
 */
function computeChase(gameRaw) {
  const periods = gameRaw?.periods || [];
  if (periods.length < 2) return null;
  const first = periods[0];
  const second = periods[periods.length - 1];
  if (!first || !second) return null;

  const firstBat = (first.teams || []).find((t) => t.discipline === "BATTING");
  const secondBat = (second.teams || []).find((t) => t.discipline === "BATTING");
  if (!firstBat || !secondBat) return null;

  const firstStats = _stats(firstBat);
  const secondStats = _stats(secondBat);
  const firstScore = firstStats.TOTAL_SCORE;
  const chaseScore = secondStats.TOTAL_SCORE ?? 0;
  const oversBowled = secondStats.TOTAL_OVERS;
  const overLimit = secondStats.OVER_LIMIT ?? firstStats.OVER_LIMIT;
  if (firstScore == null || overLimit == null) return null;

  const target = firstScore + 1;
  const needed = target - chaseScore;
  // Chase already achieved (needed <= 0) or first innings still going.
  if (needed <= 0) return null;

  const ballsBowled = oversToBalls(oversBowled) ?? 0;
  const totalBalls = Math.max(0, Math.round(overLimit * 6));
  const ballsLeft = Math.max(0, totalBalls - ballsBowled);
  if (ballsLeft === 0) return null;

  // Cricket convention: talk in overs when plenty remain, balls when the
  // chase is on the wire.
  const remainderLabel = ballsLeft > 60
    ? `${Math.floor(ballsLeft / 6)} overs`
    : `${ballsLeft} ball${ballsLeft === 1 ? "" : "s"}`;

  // Required run rate — runs needed per over remaining. Formatted to two
  // decimals so it matches the way commentators typically read it out
  // ("RRR 7.36"). Guarded against zero-overs-left just in case.
  const oversLeft = ballsLeft / 6;
  const requiredRunRate = oversLeft > 0 ? +(needed / oversLeft).toFixed(2) : null;

  return {
    chasingTeamId: secondBat.id,
    target,
    needed,
    ballsLeft,
    remainderLabel,
    requiredRunRate,
  };
}

// ---------------------------------------------------------------------------
// One live tile. Sized so 1.5 tiles are visible on a 390px mobile viewport,
// nudging users to scroll horizontally when multiple games are live.
// ---------------------------------------------------------------------------
function LiveGameTile({ game, teamMap, gradeMap, clubIds, onOpen }) {
  const home = (game.teams || []).find((t) => t.isHomeTeam) || (game.teams || [])[0] || {};
  const away = (game.teams || []).find((t) => !t.isHomeTeam) || (game.teams || [])[1] || {};
  const scores = scoresByTeam(game.raw);
  const gradeName = gradeMap[game.grade_id]?.name;
  const chase = computeChase(game.raw);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(game.playhq_id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen?.(game.playhq_id);
        }
      }}
      className="snap-start shrink-0 w-[240px] sm:w-[260px] p-3 rounded-2xl border-red-500/40 bg-red-500/[0.04] hover:border-red-500/60 hover-lift transition-colors cursor-pointer"
      data-testid={`live-score-tile-${game.playhq_id}`}
    >
      {gradeName && (
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground truncate">
          {gradeName}
        </div>
      )}
      <TeamRow
        teamRef={home}
        teamMap={teamMap}
        clubIds={clubIds}
        innings={scores[home.id]}
        chase={chase?.chasingTeamId === home.id ? chase : null}
        gameId={game.playhq_id}
      />
      <TeamRow
        teamRef={away}
        teamMap={teamMap}
        clubIds={clubIds}
        innings={scores[away.id]}
        chase={chase?.chasingTeamId === away.id ? chase : null}
        gameId={game.playhq_id}
      />
    </Card>
  );
}

function TeamRow({ teamRef, teamMap, clubIds, innings, chase, gameId }) {
  const team = teamMap[teamRef?.id];
  const name = team?.name || "TBC";
  const logo = pickLogo(team?.raw);
  const isClub = clubIds.has(teamRef?.id);

  // Show the latest innings only — live tile is a peek, not the full card.
  const list = Array.isArray(innings) ? innings : innings ? [innings] : [];
  const latest = list.length > 0 ? list[list.length - 1] : null;
  const scoreText = latest ? formatInnings(latest) : "—";
  const crr = currentRunRate(latest);

  return (
    <div className="mt-2 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        {logo ? (
          <img
            src={logo}
            alt=""
            className="h-6 w-6 rounded-full object-cover shrink-0 bg-[hsl(var(--secondary))]"
            loading="lazy"
          />
        ) : (
          <div className="h-6 w-6 rounded-full bg-[hsl(var(--secondary))] shrink-0" />
        )}
        <div className={`text-sm truncate ${isClub ? "font-semibold" : "font-medium text-foreground/85"}`}>
          {name}
        </div>
        <div className="ml-auto flex flex-col items-end shrink-0 leading-tight">
          <span className="font-mono tabular-nums text-sm">{scoreText}</span>
          {crr != null && (
            <span
              className="font-mono tabular-nums text-[10px] text-muted-foreground"
              data-testid={gameId ? `live-score-crr-${gameId}-${teamRef?.id}` : undefined}
              title="Current run rate"
            >
              RR {crr.toFixed(2)}
            </span>
          )}
        </div>
      </div>
      {chase && (
        <div
          className="mt-1 pl-8 text-[11px] leading-tight tabular-nums text-red-600/90"
          data-testid={gameId ? `live-score-chase-${gameId}` : undefined}
        >
          <span className="font-semibold">target {chase.target}</span>
          <span className="mx-1.5 text-muted-foreground/60">·</span>
          <span>needs {chase.needed} off {chase.remainderLabel}</span>
          {chase.requiredRunRate != null && (
            <>
              <span className="mx-1.5 text-muted-foreground/60">·</span>
              {(() => {
                // Compare the chasing team's CRR to the required rate so
                // the pill reads as a live "on / off pace" signal.
                //   • CRR ≥ RRR → chase ahead of pace (green)
                //   • CRR <  RRR → chase behind pace   (amber)
                //   • No CRR yet → parity/neutral      (default red)
                let cls = "";
                let state = "even";
                if (crr != null) {
                  if (crr >= chase.requiredRunRate) {
                    cls = "text-emerald-600";
                    state = "ahead";
                  } else {
                    cls = "text-amber-600";
                    state = "behind";
                  }
                }
                return (
                  <span
                    className={`font-semibold ${cls}`}
                    data-testid={gameId ? `live-score-rrr-${gameId}` : undefined}
                    data-chase-state={state}
                    title={
                      state === "ahead"
                        ? "Chase ahead of the required run rate"
                        : state === "behind"
                          ? "Chase behind the required run rate"
                          : "Required run rate"
                    }
                  >
                    RRR {chase.requiredRunRate.toFixed(2)}
                  </span>
                );
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
}
